"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Secure() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: illoRef,
    isVisible: illoVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const reassurances = ["AI-Powered Threat Detection", "24/7 Security Monitoring", "Proactive Risk Management"];
  return <section data-section-id="1788" id="secure-your-future" className="relative overflow-hidden bg-background py-24 md:py-48">
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundColor: "hsl(230 20% 4%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute pointer-events-none -bottom-40 -right-40 md:-bottom-56 md:-right-56 w-[520px] h-[460px] md:w-[760px] md:h-[680px]" aria-hidden="true">
        <svg viewBox="0 0 760 680" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
          <path d="M620,80 C720,180 740,320 700,440 C660,560 540,640 400,650 C260,660 130,600 70,480 C10,360 40,210 150,120 C260,30 410,-20 520,20 C570,38 580,40 620,80 Z" fill="hsl(185 100% 50%)" opacity="0.1" />
        </svg>
      </div>

      {}
      <div className="absolute pointer-events-none hidden lg:block right-[6%] bottom-[8%] select-none" aria-hidden="true">
        <span className="font-default italic text-white" style={{
        fontSize: "clamp(180px, 22vw, 320px)",
        opacity: 0.05,
        lineHeight: 1,
        fontWeight: 700
      }}>
          01
        </span>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-end">
          {}
          <div className="lg:col-span-8 relative">
            {}
            <div ref={headerRef} className="transition-all duration-700 ease-out" style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(14px)"
          }}>
              <div className="flex items-center gap-4">
                <span className="font-mono uppercase text-secondary" style={{
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: "0.2em",
                lineHeight: 1
              }}>
                  § 01 — Secure Your Future
                </span>
              </div>
              <div className="mt-4 flex items-center gap-2" aria-hidden="true">
                <div className="h-px w-16 bg-border" />
                <div className="h-1 w-1 rounded-lg bg-primary" />
              </div>
            </div>

            {}
            <h2 ref={headlineRef} className="font-default text-white mt-10 md:mt-14 transition-all duration-700 ease-out" style={{
            fontSize: "clamp(2.75rem, 5.4vw + 1rem, 5.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.04em",
            lineHeight: 1.0,
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(14px)"
          }}>
              Elite AI-Driven{" "}
              <em className="font-default not-italic text-primary" style={{
              fontStyle: "italic",
              fontWeight: 700
            }}>
                Cybersecurity
              </em>{" "}
              for the Modern Enterprise.
            </h2>

            {}
            <div ref={ctaRef} className="mt-10 md:mt-12 transition-all duration-700 ease-out" style={{
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? "translateY(0)" : "translateY(14px)",
            transitionDelay: "120ms"
          }}>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                <Link to="/ContactPage" className="group relative rounded-lg px-7 py-6 h-auto text-base bg-primary text-black hover:bg-white transition-all duration-200" style={{
                fontWeight: 600,
                letterSpacing: "0.005em"
              }}>
                  <span className="relative z-10">Request a Free Security Assessment</span>
                </Link>

                <Link to="/ContactPage" className="rounded-lg px-7 py-6 h-auto text-base bg-transparent text-white border border-border hover:bg-secondary/20 transition-colors duration-200" style={{
                fontWeight: 500
              }}>
                  Contact Our Experts
                </Link>
              </div>

              {}
              <ul className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-3">
                {reassurances.map((item, i) => <li data-index={i} key={i} className="flex items-center gap-3 text-secondary">
                    <span className="inline-block h-1 w-1 rounded-lg shrink-0 bg-primary" aria-hidden="true" />
                    <span className="font-mono uppercase" style={{
                  fontSize: "0.6875rem",
                  letterSpacing: "0.12em",
                  lineHeight: 1.4,
                  fontWeight: 500
                }}>
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div ref={illoRef} className="lg:col-span-4 flex justify-center lg:justify-end items-end relative transition-all duration-700 ease-out" style={{
          opacity: illoVisible ? 1 : 0,
          transform: illoVisible ? "translateY(0)" : "translateY(14px)",
          transitionDelay: "200ms"
        }}>
            <div className="relative" style={{
            width: "min(100%, 280px)"
          }}>
              {}
              <svg viewBox="0 0 280 280" className="absolute inset-0 w-full h-full -z-0" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
                <path d="M220,40 C250,80 260,140 240,190 C220,240 160,270 110,260 C60,250 20,200 15,150 C10,100 40,50 90,30 C140,10 200,15 220,40 Z" fill="hsl(230 15% 15%)" opacity="0.8" />
              </svg>

              {}
              <div className="relative" style={{
              width: "min(100%, 280px)",
              aspectRatio: "1 / 1"
            }}>
                <svg viewBox="0 0 280 280" className="absolute inset-0 w-full h-full" fill="none" stroke="none" aria-label="Cybersecurity shield icon" role="img">
                  <path d="M140 40 L 220 80 L 220 160 C 220 210, 140 250, 140 250 C 140 250, 60 210, 60 160 L 60 80 Z" stroke="hsl(185 100% 50%)" strokeWidth="4" fill="none" />
                  <path d="M100 130 L 130 160 L 180 110" stroke="hsl(185 100% 50%)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}